import type { ApiObject } from "@rudderstack/analytics-js";
import { RudderAnalytics } from "@rudderstack/analytics-js";
import * as Sentry from "@sentry/browser";
import { getFeatureFlag, isBoolean } from "@sinch/feature-flags";
import { atom } from "jotai";
import { globalJotaiStore } from "../jotai-store";
import {
  rudderStackClientEventZod,
  type RudderStackClientEvent,
} from "./rudderstack/eventTypes";

const rudderanalytics = new RudderAnalytics();

export const rudderstackReadyAtom = atom(false);

export const initRudderstack = (
  startSession = false,
  dataPlaneUrl: string,
  writeKey: string
) => {
  try {
    const rudderStackEnabled = getFeatureFlag(
      "useRudderStack",
      isBoolean,
      false
    );
    if (!rudderStackEnabled) {
      return Promise.resolve();
    }
    rudderanalytics.load(writeKey, dataPlaneUrl, {
      storage: { type: "localStorage" }, // Avoid sending cookies to the server all the time.
      sessions: { autoTrack: startSession },
    });
    rudderanalytics.ready(() => {
      globalJotaiStore.set(rudderstackReadyAtom, true);
    });
    return Promise.resolve();
  } catch (error) {
    Sentry.captureException(error);
    console.error(error);
    return Promise.reject();
  }
};

export const startRudderstackSession = (hasSomeConsent: boolean) => {
  try {
    if (hasSomeConsent) {
      rudderanalytics.startSession();
    }
  } catch (e) {
    console.error(e);
  }

  return rudderanalytics.endSession;
};

export const identifyRudderStackUser = (userId: string, traits?: ApiObject) =>
  rudderanalytics.identify(userId, { service: "Shell", ...traits });

export const groupRudderStackUser = (accountId: string, traits?: ApiObject) =>
  rudderanalytics.group(accountId, traits);

export const trackRudderStackPage = () => rudderanalytics.page();

export const trackRudderStackEvent = (
  eventId: string,
  properties: RudderStackClientEvent
) => {
  const parsedEvent = rudderStackClientEventZod.safeParse(properties);
  if (!parsedEvent.success) {
    Sentry.captureException(parsedEvent.error);
  }

  rudderanalytics.track(eventId, properties, {
    integrations: {
      All: true,
      SNOWFLAKE: {
        // Use SNOWFLAKE for Snowflake, RS for Redshift, BQ for BigQuery, POSTGRES for PostgreSQL.
        options: {
          // This doesn't seem to work, but we have a transofrmation on rudderstack doing the same and that seemed to work
          jsonPaths: ["track.properties.CUSTOM", "track.properties.custom"],
        },
      },
    },
  });
};

export const getRudderStackAnonymousId = () => rudderanalytics.getAnonymousId();

export const RUDDERSTACK_PERSISTED_KEYS = ["rl_anonymous_id"];
